import { booleanAttribute } from "#ce/utils/props";

const typeOptions = ["success", "info", "warning", "error"];
const variantOptions = ["elevated", "flat", "outlined", "text"];
const appendAlignOptions = ["start", "center", "end"];

export const props = Object.freeze({
  /**
   * Semantic intent of the alert. "info" uses the neutral info palette, "success" green, "warning" yellow, "error" red. Leave unset for a plain surface that picks up the default alert tokens.
   *
   * @type {String}
   * @default undefined
   * @values success, info, warning, error
   * @storybook-control select
   */
  type: {
    type: String,
    default: undefined,
    validator: (value) => typeOptions.includes(value),
  },
  /**
   * Message body rendered inside the content slot. Ignored when default slot content is supplied.
   *
   * @type {String}
   * @default undefined
   */
  text: {
    type: String,
    default: undefined,
  },
  /**
   * Visual treatment. "elevated" adds a drop shadow and lifts the alert above sibling content; "flat" sits flush on the surface; "outlined" reserved for future use; "text" strips the background and padding so the alert reads as inline coloured text.
   *
   * @type {String}
   * @default undefined
   * @values elevated, flat, outlined, text
   * @storybook-control select
   */
  variant: {
    type: String,
    default: undefined,
    validator: (value) => variantOptions.includes(value),
  },
  /**
   * Cross-axis alignment of the append slot relative to the message body. "start" pins to the top, "center" centres vertically, "end" pins to the bottom.
   *
   * @type {String}
   * @default undefined
   * @values start, center, end
   * @storybook-control select
   */
  appendAlign: {
    type: String,
    default: undefined,
    validator: (value) => appendAlignOptions.includes(value),
  },
  /**
   * Tighten the padding, icon size, and typography for inline / toast-style messages.
   *
   * @type {Boolean|String}
   * @default false
   */
  compact: booleanAttribute(),
  /**
   * Render a close (x) affordance in the top-left corner. Clicking it emits close and removes the alert.
   *
   * @type {Boolean|String}
   * @default false
   */
  closeable: booleanAttribute(),
  /**
   * Switch the alert to position: fixed so it can sit pinned to the viewport.
   *
   * @type {Boolean|String}
   * @default false
   */
  fixed: booleanAttribute(),
  /**
   * Render as a flash banner: fixed near the top of the viewport, horizontally centred, above other content. Use sparingly for transient notifications.
   *
   * @type {Boolean|String}
   * @default false
   */
  flash: booleanAttribute(),
  /**
   * Show the default icon for the current alert type in the prepend slot. For a custom glyph, use the prepend slot instead.
   *
   * @type {Boolean|String}
   * @default false
   */
  icon: booleanAttribute(),
});
